import cn from 'classnames'
import React, {
  ChangeEvent,
  ClipboardEvent,
  KeyboardEvent,
  useCallback,
  useId,
  useRef,
} from 'react'

import style from './CodeInput.style'

export type CodeInputProps = {
  className?: string
  /** Number of digit inputs to render */
  length?: number
  /** Current value as a string of digits */
  value: string
  /** Called with the updated value string on every change */
  onChange: (value: string) => void
  /** Label text displayed above the inputs */
  label?: string
  /** Error text displayed below the inputs */
  errorText?: React.ReactNode
  /** Disables all inputs */
  disabled?: boolean
  testId?: string
}

export default function CodeInput(props: CodeInputProps): React.ReactElement {
  const {
    className = '',
    length = 6,
    value,
    onChange,
    label,
    errorText,
    disabled = false,
    testId,
  } = props

  const reactId = useId()
  const errorId = `${reactId}-error`
  const inputsRef = useRef<(HTMLInputElement | null)[]>([])

  const digits = value.split('').concat(Array(length).fill('')).slice(0, length)

  const focusInput = useCallback(
    (index: number) => {
      if (index >= 0 && index < length) {
        inputsRef.current[index]?.focus()
      }
    },
    [length],
  )

  const updateValue = useCallback(
    (index: number, digit: string) => {
      const newDigits = [...digits]
      newDigits[index] = digit
      onChange(newDigits.join('').replace(/[^\d]/g, ''))
    },
    [digits, onChange],
  )

  const handleChange = useCallback(
    (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
      const inputVal = e.target.value.replace(/[^\d]/g, '')
      if (!inputVal) return

      // Take only the last digit typed (handles overwrite)
      const digit = inputVal.slice(-1)
      updateValue(index, digit)

      if (index < length - 1) {
        focusInput(index + 1)
      }
    },
    [updateValue, focusInput, length],
  )

  const handleKeyDown = useCallback(
    (index: number) => (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace') {
        e.preventDefault()
        if (digits[index]) {
          updateValue(index, '')
        } else if (index > 0) {
          updateValue(index - 1, '')
          focusInput(index - 1)
        }
      } else if (e.key === 'ArrowLeft' && index > 0) {
        e.preventDefault()
        focusInput(index - 1)
      } else if (e.key === 'ArrowRight' && index < length - 1) {
        e.preventDefault()
        focusInput(index + 1)
      }
    },
    [digits, updateValue, focusInput, length],
  )

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault()
      const pasted = e.clipboardData.getData('text').replace(/[^\d]/g, '')
      if (!pasted) return

      const pastedDigits = pasted.slice(0, length)
      onChange(pastedDigits)

      // Focus the next empty input or the last input
      const nextIndex = Math.min(pastedDigits.length, length - 1)
      focusInput(nextIndex)
    },
    [onChange, focusInput, length],
  )

  return (
    <div
      className={cn('CodeInput', className, { error: !!errorText })}
      css={style}
      data-testid={testId}
    >
      {label && <div className="code-input-label">{label}</div>}
      <div className="code-input-fields" role="group" aria-label={label}>
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(el) => {
              inputsRef.current[index] = el
            }}
            className={cn('code-input-digit', {
              disabled,
              filled: Boolean(digit),
            })}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            value={digit}
            disabled={disabled}
            onChange={handleChange(index)}
            onKeyDown={handleKeyDown(index)}
            onPaste={handlePaste}
            aria-label={`Digit ${index + 1} of ${length}`}
            aria-invalid={!!errorText || undefined}
            aria-describedby={errorText ? errorId : undefined}
            data-testid={testId ? `${testId}-digit-${index}` : undefined}
            autoComplete="one-time-code"
          />
        ))}
      </div>
      {errorText && (
        <div id={errorId} role="alert" className="code-input-error">
          {errorText}
        </div>
      )}
    </div>
  )
}
